import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import {
  LOADING_CYCLE_MS,
  LOADING_CROSSFADE_MS,
  LOADING_IMAGE_RETRY_MS,
  LOADING_INITIAL_CYCLE_MS,
  LOADING_READY_GATE_MAX_MS,
  LOADING_TEXT_SLIDE_MS,
} from './utils/loading.constants';
import {
  areAllLoadingImagesCached,
  ensureLoadingImageLoaded,
  isLoadingImageReady,
  preloadLoadingImage,
  preloadLoadingImages,
  seedLoadedSrcsFromPreloadCache,
  waitForCriticalLoadingImages,
} from './utils/loading.preload';
import {
  getElapsedOnCurrentSlide,
  getRestoredImageIndex,
  getRestoredMessageIndex,
  markCarouselSlideShown,
  persistCarouselSession,
  shouldRestoreCarouselSession,
} from './utils/carouselSession';
import './styles/VerticalLoadingScreen.css';

/** Double rAF so the browser paints initial layer state before crossfade classes apply. */
function startCrossfadeAnimation(onStart) {
  requestAnimationFrame(() => {
    requestAnimationFrame(onStart);
  });
}

/**
 * Vertical carousel loader — text advances on timer; images crossfade only when loaded.
 */
const VerticalLoadingScreen = ({
  active = true,
  images = [],
  messages = [],
  cycleMs = LOADING_CYCLE_MS,
  initialCycleMs = LOADING_INITIAL_CYCLE_MS,
  crossfadeMs = LOADING_CROSSFADE_MS,
  textSlideMs = LOADING_TEXT_SLIDE_MS,
}) => {
  const imageCount = images.length;
  const messageCount = messages.length;
  const cycleItemCount = messageCount > 1 ? messageCount : imageCount;

  const messageIndexRef = useRef(0);
  const imageIndexRef = useRef(0);
  const textCrossfadeEndRef = useRef(null);
  const imageCrossfadeEndRef = useRef(null);
  const cycleTimeoutRef = useRef(null);
  const imageRetryTimeoutRef = useRef(null);
  const isTextTransitioningRef = useRef(false);
  const isImageTransitioningRef = useRef(false);
  const loadedSrcsRef = useRef(new Set());
  const allImagesReadyRef = useRef(false);
  const isFirstCycleRef = useRef(true);
  const targetImageIndexRef = useRef(null);

  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [textTransitionTarget, setTextTransitionTarget] = useState(null);
  const [isTextCrossfading, setIsTextCrossfading] = useState(false);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageTransitionTarget, setImageTransitionTarget] = useState(null);
  const [isImageCrossfading, setIsImageCrossfading] = useState(false);

  const getImageSrc = useCallback(
    (index) => (imageCount ? images[index % imageCount] : ''),
    [imageCount, images]
  );

  const getMessage = useCallback(
    (index) => (messageCount ? messages[index % messageCount] : ''),
    [messageCount, messages]
  );

  const isTextTransitioning = textTransitionTarget !== null;
  const textOutgoingIndex = isTextTransitioning ? currentMessageIndex : null;
  const textIncomingIndex = isTextTransitioning ? textTransitionTarget : currentMessageIndex;

  const isImageTransitioning = imageTransitionTarget !== null;
  const imageOutgoingIndex = isImageTransitioning ? currentImageIndex : null;
  const imageIncomingIndex = isImageTransitioning ? imageTransitionTarget : currentImageIndex;

  const clearTimers = useCallback(() => {
    if (textCrossfadeEndRef.current) {
      window.clearTimeout(textCrossfadeEndRef.current);
      textCrossfadeEndRef.current = null;
    }
    if (imageCrossfadeEndRef.current) {
      window.clearTimeout(imageCrossfadeEndRef.current);
      imageCrossfadeEndRef.current = null;
    }
    if (cycleTimeoutRef.current) {
      window.clearTimeout(cycleTimeoutRef.current);
      cycleTimeoutRef.current = null;
    }
    if (imageRetryTimeoutRef.current) {
      window.clearTimeout(imageRetryTimeoutRef.current);
      imageRetryTimeoutRef.current = null;
    }
  }, []);

  const prefetchUpcomingImages = useCallback(
    (fromImageIndex) => {
      if (!imageCount) return;

      const upcoming = [
        images[(fromImageIndex + 1) % imageCount],
        images[(fromImageIndex + 2) % imageCount],
      ];

      upcoming.forEach((src) => {
        preloadLoadingImage(src)
          .then((loadedSrc) => {
            loadedSrcsRef.current.add(loadedSrc);
          })
          .catch(() => {});
      });
    },
    [imageCount, images]
  );

  const finishTextCrossfade = useCallback((nextIndex) => {
    messageIndexRef.current = nextIndex;
    setCurrentMessageIndex(nextIndex);
    setTextTransitionTarget(null);
    setIsTextCrossfading(false);
    isTextTransitioningRef.current = false;
    markCarouselSlideShown(messageIndexRef.current, imageIndexRef.current);
  }, []);

  const runTextCrossfade = useCallback(
    (nextIndex) => {
      if (isTextTransitioningRef.current || !messageCount) return;

      isTextTransitioningRef.current = true;
      setTextTransitionTarget(nextIndex);
      setIsTextCrossfading(false);

      startCrossfadeAnimation(() => {
        setIsTextCrossfading(true);
      });

      textCrossfadeEndRef.current = window.setTimeout(() => {
        finishTextCrossfade(nextIndex);
      }, crossfadeMs);
    },
    [crossfadeMs, finishTextCrossfade, messageCount]
  );

  const tryAdvanceImageRef = useRef(null);

  const finishImageCrossfade = useCallback(
    (nextIndex) => {
      imageIndexRef.current = nextIndex;
      setCurrentImageIndex(nextIndex);
      setImageTransitionTarget(null);
      setIsImageCrossfading(false);
      isImageTransitioningRef.current = false;
      markCarouselSlideShown(messageIndexRef.current, imageIndexRef.current);
      prefetchUpcomingImages(nextIndex);

      const pending = targetImageIndexRef.current;
      if (pending !== null && pending !== nextIndex) {
        targetImageIndexRef.current = null;
        window.setTimeout(() => tryAdvanceImageRef.current?.(pending), 0);
      } else {
        targetImageIndexRef.current = null;
      }
    },
    [prefetchUpcomingImages]
  );

  const runImageCrossfade = useCallback(
    (nextIndex) => {
      if (isImageTransitioningRef.current || !imageCount) return;
      if (nextIndex === imageIndexRef.current) return;

      isImageTransitioningRef.current = true;
      prefetchUpcomingImages(nextIndex);

      setImageTransitionTarget(nextIndex);
      setIsImageCrossfading(false);

      startCrossfadeAnimation(() => {
        setIsImageCrossfading(true);
      });

      imageCrossfadeEndRef.current = window.setTimeout(() => {
        finishImageCrossfade(nextIndex);
      }, crossfadeMs);
    },
    [crossfadeMs, finishImageCrossfade, imageCount, prefetchUpcomingImages]
  );

  const tryAdvanceImage = useCallback(
    (targetImageIndex) => {
      if (!active || imageCount <= 1) return;

      if (isImageTransitioningRef.current) {
        targetImageIndexRef.current = targetImageIndex;
        return;
      }

      if (targetImageIndex === imageIndexRef.current) {
        return;
      }

      const src = images[targetImageIndex];

      const attemptLoad = () => {
        if (!active) return;

        if (loadedSrcsRef.current.has(src) || isLoadingImageReady(src)) {
          loadedSrcsRef.current.add(src);
          runImageCrossfade(targetImageIndex);
          return;
        }

        ensureLoadingImageLoaded(src)
          .then((loaded) => {
            if (!active) return;
            loadedSrcsRef.current.add(loaded);
            if (!isImageTransitioningRef.current) {
              runImageCrossfade(targetImageIndex);
            }
          })
          .catch(() => {
            if (!active) return;
            imageRetryTimeoutRef.current = window.setTimeout(
              attemptLoad,
              LOADING_IMAGE_RETRY_MS
            );
          });
      };

      attemptLoad();
    },
    [active, imageCount, images, runImageCrossfade]
  );

  const scheduleNextCycle = useCallback(() => {
    if (!active || cycleItemCount <= 1) {
      return;
    }

    if (messageCount > 1 && !isTextTransitioningRef.current) {
      const nextMessageIndex = (messageIndexRef.current + 1) % messageCount;
      runTextCrossfade(nextMessageIndex);
    }

    if (imageCount > 1) {
      const nextImageIndex = (imageIndexRef.current + 1) % imageCount;
      tryAdvanceImage(nextImageIndex);
    }

    isFirstCycleRef.current = false;
  }, [
    active,
    cycleItemCount,
    imageCount,
    messageCount,
    runTextCrossfade,
    tryAdvanceImage,
  ]);

  useEffect(() => {
    if (!active || !imageCount) {
      if (!active) {
        loadedSrcsRef.current = new Set();
        allImagesReadyRef.current = false;
      }
      return undefined;
    }

    seedLoadedSrcsFromPreloadCache(images, loadedSrcsRef.current);
    allImagesReadyRef.current = areAllLoadingImagesCached(images);

    let cancelled = false;

    preloadLoadingImages(images).then((results) => {
      if (cancelled) return;
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          loadedSrcsRef.current.add(result.value);
        }
      });
      allImagesReadyRef.current = areAllLoadingImagesCached(images);
    });

    return () => {
      cancelled = true;
    };
  }, [active, imageCount, images]);

  useEffect(() => {
    if (!active) {
      clearTimers();
      messageIndexRef.current = 0;
      imageIndexRef.current = 0;
      isTextTransitioningRef.current = false;
      isImageTransitioningRef.current = false;
      isFirstCycleRef.current = true;
      targetImageIndexRef.current = null;
      setCurrentMessageIndex(0);
      setCurrentImageIndex(0);
      setTextTransitionTarget(null);
      setImageTransitionTarget(null);
      setIsTextCrossfading(false);
      setIsImageCrossfading(false);
      return undefined;
    }

    if (cycleItemCount <= 1) {
      return undefined;
    }

    const restored = shouldRestoreCarouselSession();
    const startMessage = getRestoredMessageIndex(messageCount);
    const startImage = getRestoredImageIndex(imageCount);

    messageIndexRef.current = startMessage;
    imageIndexRef.current = startImage;
    setCurrentMessageIndex(startMessage);
    setCurrentImageIndex(startImage);
    isFirstCycleRef.current = startMessage === 0;

    if (!restored) {
      markCarouselSlideShown(startMessage, startImage);
    }

    seedLoadedSrcsFromPreloadCache(images, loadedSrcsRef.current);
    allImagesReadyRef.current = areAllLoadingImagesCached(images);

    let cancelled = false;

    const queueCycle = (delayMs) => {
      cycleTimeoutRef.current = window.setTimeout(() => {
        if (cancelled) return;
        scheduleNextCycle();
        queueCycle(cycleMs);
      }, delayMs);
    };

    const startTimerLoop = () => {
      if (cancelled) return;

      const elapsed = restored ? getElapsedOnCurrentSlide() : 0;
      const baseDelay = isFirstCycleRef.current ? initialCycleMs : cycleMs;
      const firstDelay = Math.max(0, baseDelay - elapsed);

      queueCycle(firstDelay);
    };

    preloadLoadingImages(images).then((results) => {
      if (cancelled) return;
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          loadedSrcsRef.current.add(result.value);
        }
      });
      allImagesReadyRef.current = areAllLoadingImagesCached(images);
    });

    waitForCriticalLoadingImages(
      images[0],
      images[1],
      LOADING_READY_GATE_MAX_MS
    ).finally(() => {
      if (!cancelled) startTimerLoop();
    });

    return () => {
      cancelled = true;
      persistCarouselSession(messageIndexRef.current, imageIndexRef.current);
      clearTimers();
    };
  }, [
    active,
    clearTimers,
    cycleItemCount,
    cycleMs,
    images,
    imageCount,
    initialCycleMs,
    messageCount,
    scheduleNextCycle,
  ]);

  tryAdvanceImageRef.current = tryAdvanceImage;

  const renderImageLayer = (index, role) => {
    const src = getImageSrc(index);
    if (!src) return null;

    const layerClass = [
      'fsl-image',
      'fsl-image-layer',
      role === 'outgoing'
        ? (isImageCrossfading ? 'is-exiting' : 'is-visible')
        : (isImageTransitioning
          ? (isImageCrossfading ? 'is-visible' : 'is-entering')
          : 'is-visible'),
    ].join(' ');

    return (
      <img
        key={`${role}-${index}`}
        src={src}
        alt=""
        className={layerClass}
        draggable={false}
        decoding="async"
        onLoad={() => {
          loadedSrcsRef.current.add(src);
        }}
        onError={() => {}}
      />
    );
  };

  const renderTextLayer = (index, role) => {
    const message = getMessage(index);
    if (!message) return null;

    const layerClass = [
      'fsl-text-line',
      role === 'outgoing'
        ? (isTextCrossfading ? 'is-exiting' : 'is-visible')
        : (isTextTransitioning
          ? (isTextCrossfading ? 'is-visible' : 'is-entering')
          : 'is-visible'),
    ].join(' ');

    return (
      <p
        key={`${role}-${index}`}
        className={layerClass}
        aria-hidden={role === 'outgoing' && isTextCrossfading ? 'true' : undefined}
      >
        {message}
      </p>
    );
  };

  const showImages = imageCount > 0;
  const showMessages = messageCount > 0;

  if (!showImages && !showMessages) {
    return null;
  }

  return (
    <Box
      className="fsl-root"
      role="status"
      aria-live="polite"
      aria-busy="true"
      sx={{
        background: 'transparent',
        '--fsl-crossfade-ms': `${crossfadeMs}ms`,
        '--fsl-text-ms': `${textSlideMs}ms`,
      }}
    >
      <div className="fsl-inner">
        {showImages && (
          <div className="fsl-image-stage" aria-hidden="true">
            {isImageTransitioning ? (
              <>
                {renderImageLayer(imageOutgoingIndex, 'outgoing')}
                {renderImageLayer(imageIncomingIndex, 'incoming')}
              </>
            ) : (
              renderImageLayer(currentImageIndex, 'incoming')
            )}
          </div>
        )}

        {showMessages && (
          <div className="fsl-text-viewport">
            {isTextTransitioning ? (
              <>
                {renderTextLayer(textOutgoingIndex, 'outgoing')}
                {renderTextLayer(textIncomingIndex, 'incoming')}
              </>
            ) : (
              renderTextLayer(currentMessageIndex, 'incoming')
            )}
          </div>
        )}
      </div>
    </Box>
  );
};

export default VerticalLoadingScreen;
